import type { Customer, CustomerVo, MemberPermission } from "@zhihaoscm/entity";
import { ROLE_CODE } from "@zhihaoscm/meta";
import { create } from "zustand";
import { USER_CONTEXT_KEY } from "@/api/runtime";

export const ACCOUNT_CONTEXT_KEY = "zhihao-web-account";

/** 旧 Storage 可能损坏，初始化时必须回退为空对象而不是阻断应用。 */
function readStorage<T extends object>(key: string): Partial<T> {
    try { return JSON.parse(localStorage.getItem(key) || "{}"); }
    catch { return {}; }
}

export enum AccountType {
    COMMON = 1,
    ROOT_ACCOUNT,
    SUB_ACCOUNT
}

interface UserSessionState {
    allUnreadMsgNum: number;
    authorizationState?: number;
    bindTime: string;
    email: string;
    isOldUser?: number;
    mobile: string;
    nickName: string;
    passwordUpdateTime: string;
    proxyAccountId?: number;
    roleCode?: number;
    userInfo: CustomerVo;
    zhihaoWebToken: string;
    clear: () => void;
    setToken: (token: string) => void;
    setUser: (userInfo: CustomerVo, metadata: Partial<UserSessionState>) => void;
}

const storedUser = readStorage<UserSessionState>(USER_CONTEXT_KEY);

export const useUserSession = create<UserSessionState>((set) => ({
    allUnreadMsgNum: storedUser.allUnreadMsgNum ?? 0,
    authorizationState: storedUser.authorizationState,
    bindTime: storedUser.bindTime ?? "",
    email: storedUser.email ?? "",
    isOldUser: storedUser.isOldUser,
    mobile: storedUser.mobile ?? "",
    nickName: storedUser.nickName ?? "",
    passwordUpdateTime: storedUser.passwordUpdateTime ?? "",
    proxyAccountId: storedUser.proxyAccountId,
    roleCode: storedUser.roleCode,
    userInfo: storedUser.userInfo ?? ({ customer: {} } as CustomerVo),
    zhihaoWebToken: storedUser.zhihaoWebToken ?? "",
    clear: () => set({
        allUnreadMsgNum: 0,
        authorizationState: 0,
        bindTime: "",
        email: "",
        nickName: "",
        passwordUpdateTime: "",
        userInfo: { customer: {} } as CustomerVo,
        zhihaoWebToken: ""
    }),
    setToken: (zhihaoWebToken) => set({ zhihaoWebToken }),
    setUser: (userInfo, metadata) => set({ ...metadata, userInfo })
}));

interface AccountState {
    accountType?: number;
    actualAccount: Customer;
    actualMemberPermission: MemberPermission;
    proxyActualAccount: Customer;
    proxyMemberPermission: MemberPermission;
    setFromUser: (userInfo: CustomerVo) => void;
}

const storedAccount = readStorage<AccountState>(ACCOUNT_CONTEXT_KEY);

export const useAccountSession = create<AccountState>((set) => ({
    accountType: storedAccount.accountType,
    actualAccount: storedAccount.actualAccount ?? ({} as Customer),
    actualMemberPermission: storedAccount.actualMemberPermission ?? ({} as MemberPermission),
    proxyActualAccount: storedAccount.proxyActualAccount ?? ({} as Customer),
    proxyMemberPermission: storedAccount.proxyMemberPermission ?? ({} as MemberPermission),
    setFromUser: (userInfo) => {
        const proxyAccount = (userInfo as any).proxyAccount as Customer | undefined;
        let accountType = AccountType.COMMON;
        if (proxyAccount && proxyAccount.id !== userInfo.customer.id) accountType = AccountType.SUB_ACCOUNT;
        else if (proxyAccount?.id === userInfo.customer.id && userInfo.customer.institutionName) accountType = AccountType.ROOT_ACCOUNT;
        set({
            accountType,
            actualAccount: userInfo.customer,
            actualMemberPermission: userInfo.memberPermission,
            proxyActualAccount: proxyAccount ?? ({} as Customer),
            proxyMemberPermission: (userInfo as any).proxyMemberPermission ?? ({} as MemberPermission)
        });
    }
}));

/** 使用旧项目的扁平 JSON 格式持久化，不能使用 Zustand 默认的 state/version 包装格式。 */
useUserSession.subscribe((state) => {
    const { clear: _clear, setToken: _setToken, setUser: _setUser, ...persisted } = state;
    localStorage.setItem(USER_CONTEXT_KEY, JSON.stringify(persisted));
});

useAccountSession.subscribe((state) => {
    const { setFromUser: _setFromUser, ...persisted } = state;
    localStorage.setItem(ACCOUNT_CONTEXT_KEY, JSON.stringify(persisted));
});

export function isCustomerRole() { return useUserSession.getState().roleCode === ROLE_CODE.CUSTOMER.code; }
export function isSupplierRole() { return useUserSession.getState().roleCode === ROLE_CODE.SUPPLIER.code; }
export function isSupplyChainRole() { return useUserSession.getState().roleCode === ROLE_CODE.SUPPLY_CHAIN.code; }

/** 子账号读取代理权限，普通/主账号读取实际会员权限。 */
export function hasRuntimeMemberPermission(permission: string) {
    const account = useAccountSession.getState();
    const user = useUserSession.getState();
    const permissions = Object.keys(account.proxyMemberPermission ?? {}).length > 0
        ? account.proxyMemberPermission
        : user.userInfo.memberPermission;
    return Boolean((permissions as any)?.[permission]);
}
